import {
	faArrowLeft,
	faCartShopping,
	faCircleExclamation,
	faCreditCard,
	faImage,
	faMinus,
	faPlus,
	faSpinner,
	faTag,
	faTimes,
	faTrash,
	faTriangleExclamation,
	faTruck,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PaymentMethodTypeEnum } from '@supply-mover-2-0/core';
import type {
	PortalCartDto,
	PortalCheckoutAddressDto,
	PortalCheckoutOptionsDto,
	PortalCheckoutPaymentMethodDto,
	PortalCheckoutPaymentTypeDto,
	PortalClientCreditDto,
	PortalShippingMethodDto,
	PricedCart,
	ValidationIssue,
} from '@supply-mover-2-0/shared';
import {
	ConfirmDialog,
	PromoChip,
	ResilientImage,
	Sidebar,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ItemDesignationBadges } from '../components/ItemDesignationBadges';
import { PortalAddPaymentMethod } from '../components/PortalAddPaymentMethod';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { formatCurrency } from '../lib/formatCurrency';
import * as cartService from '../services/portalCartService';
import { useCartStore } from '../stores/cartStore';

/**
 * Combined cart + checkout page — matches v1.0 single-view pattern.
 *
 * Layout (top to bottom):
 * 1. Cart line items with quantity controls
 * 2. Shipping address selector
 * 3. Billing address selector
 * 4. Shipping method selector + disclaimer
 * 5. Payment method selection
 * 6. Customer PO field
 * 7. Credit info (if pay on account)
 * 8. Validation warnings/errors
 * 9. Order summary + Place Order button (sticky on mobile)
 *
 * All checkout options come from GET /cart/checkout-options.
 * Addresses are filtered by billtoOrShipto. Shipping address
 * changes trigger server-side recalculation.
 */
export function CartPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token } = usePortalAuth();
	const cart = useCartStore((s) => s.cart);
	const isLoading = useCartStore((s) => s.isLoading);
	const setCart = useCartStore((s) => s.setCart);
	const setLoading = useCartStore((s) => s.setLoading);

	// Checkout options from server
	const [checkoutOptions, setCheckoutOptions] =
		useState<PortalCheckoutOptionsDto | null>(null);
	const [checkoutLoading, setCheckoutLoading] = useState(false);

	// Form state
	const [shippingAddressId, setShippingAddressId] = useState<number | null>(
		null,
	);
	const [billingAddressId, setBillingAddressId] = useState<number | null>(null);
	const [shippingMethodId, setShippingMethodId] = useState<number | null>(null);
	const [paymentTypeId, setPaymentTypeId] = useState<number | null>(null);
	const [paymentMethodId, setPaymentMethodId] = useState<number | null>(null);
	const [customerPO, setCustomerPO] = useState('');

	// Add payment method sidebar
	const [showAddPaymentSidebar, setShowAddPaymentSidebar] = useState(false);

	// Submission + validation
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [validationErrors, setValidationErrors] = useState<
		Array<ValidationIssue>
	>([]);
	const [validationWarnings, setValidationWarnings] = useState<
		Array<ValidationIssue>
	>([]);

	// Bumped after cart mutations settle to trigger re-validation
	const [validationVersion, setValidationVersion] = useState(0);
	const triggerRevalidation = useCallback(() => {
		setValidationVersion((v) => v + 1);
	}, []);

	// True while any quantity updates are queued or in-flight — disables Place Order
	const [hasPendingUpdates, setHasPendingUpdates] = useState(false);

	// Clear-cart confirmation modal
	const [showClearConfirm, setShowClearConfirm] = useState(false);
	const [clearing, setClearing] = useState(false);

	// Tracks the line item currently being removed so we can swap the trash
	// icon for a spinner — without this the row sits visually unchanged
	// while the network request is in flight, looking like the click did
	// nothing.
	const [removingId, setRemovingId] = useState<number | null>(null);

	// Fetch cart on mount
	useEffect(() => {
		if (token == null) return;
		setLoading(true);
		cartService
			.getCart(token)
			.then((res) => setCart(res.data))
			.catch(() => setCart(null))
			.finally(() => setLoading(false));
	}, [token, setCart, setLoading]);

	// Refetch the cart when a line item's image URL fails — the most
	// common cause is a stale presigned S3 URL whose backing session
	// aged out. Asking for a new cart yields freshly-signed URLs.
	const refreshCartForStaleImage = useCallback(() => {
		if (token == null) return;
		void cartService
			.getCart(token)
			.then((res) => setCart(res.data))
			.catch(() => {
				/* silent — image fallback handles the visual */
			});
	}, [token, setCart]);

	// Fetch checkout options once cart is loaded and has items
	const lineItems = cart?.lineItems ?? [];

	// Lookup map: orderDetailId → priced engine line so each rendered cart row
	// can show its own discount treatment (strikethrough original + final
	// price + a chip naming the promo). Without this the customer has to
	// scroll to the Order Summary to figure out why a line is cheaper or
	// which promo earned them the savings.
	const pricedLineByDetailId = new Map<
		number,
		{
			originalSubtotal: number;
			lineDiscountTotal: number;
			finalSubtotal: number;
			appliedPromos: Array<{ promoName: string; badgeText: string }>;
		}
	>();
	for (const pl of cart?.priced?.lines ?? []) {
		pricedLineByDetailId.set(pl.orderDetailId, {
			originalSubtotal: pl.originalSubtotal,
			lineDiscountTotal: pl.lineDiscountTotal,
			finalSubtotal: pl.finalSubtotal,
			appliedPromos: pl.appliedPromos ?? [],
		});
	}

	// Track whether initial checkout options have loaded so we can distinguish
	// between the first load (pre-fill everything) and subsequent refreshes
	// (only update shipping methods since item changes affect available methods).
	const initialCheckoutLoadedRef = useRef(false);

	/** Apply auto-selection logic for shipping methods based on available options */
	const autoSelectShippingMethod = useCallback(
		(opts: PortalCheckoutOptionsDto) => {
			if (!opts.isShippingConfigured || opts.shippingMethods.length === 0) {
				setShippingMethodId(null);
				return;
			}

			// If the currently selected method is still available and not disabled, keep it
			if (shippingMethodId != null) {
				const stillValid = opts.shippingMethods.find(
					(m) => m.id === shippingMethodId && !m.isDisabled,
				);
				if (stillValid != null) return;
			}

			// Auto-select the first available (non-disabled) method
			const firstAvailable = opts.shippingMethods.find((m) => !m.isDisabled);
			setShippingMethodId(firstAvailable?.id ?? null);
		},
		[shippingMethodId],
	);

	// Initial load — pre-fills all form fields from server state
	useEffect(() => {
		if (token == null || lineItems.length === 0) return;
		if (initialCheckoutLoadedRef.current) return;

		setCheckoutLoading(true);
		cartService
			.getCheckoutOptions(token)
			.then((res) => {
				if (res.success) {
					const opts = res.data;
					setCheckoutOptions(opts);
					initialCheckoutLoadedRef.current = true;

					// Pre-fill from cart state or defaults
					setShippingAddressId(
						opts.currentShippingAddressId ??
							opts.defaultShippingAddressId ??
							opts.shippingAddresses[0]?.id ??
							null,
					);
					setBillingAddressId(
						opts.currentBillingAddressId ??
							opts.defaultBillingAddressId ??
							opts.billingAddresses[0]?.id ??
							null,
					);

					// Pre-fill shipping method: current selection, or auto-select
					if (opts.currentShippingMethodId != null) {
						setShippingMethodId(opts.currentShippingMethodId);
					} else {
						autoSelectShippingMethod(opts);
					}

					setCustomerPO(opts.currentCustomerPO ?? '');

					// Portal checkout: Credit Card, ACH, and Company Credit.
					// Cash, Check, Credit Card Reader are CRM-only.
					const ecommercePaymentTypes = opts.paymentTypes.filter((pt) =>
						['Credit Card', 'ACH', 'Company Credit'].includes(
							pt.paymentMethodType,
						),
					);

					if (ecommercePaymentTypes.length > 0) {
						setPaymentTypeId(ecommercePaymentTypes[0].id);
					}

					// Default to primary saved method for the first payment type
					const firstType = ecommercePaymentTypes[0];
					if (firstType != null) {
						const methodsForType = opts.savedPaymentMethods.filter(
							(m) => m.type === firstType.paymentMethodType,
						);
						const primary = methodsForType.find((m) => m.isPrimary);
						setPaymentMethodId(primary?.id ?? methodsForType[0]?.id ?? null);
					}
				}
			})
			.catch(() => {})
			.finally(() => setCheckoutLoading(false));
	}, [token, lineItems.length, autoSelectShippingMethod]);

	// Refresh checkout options after cart mutations (item add/remove/qty change).
	// Item changes affect available shipping methods because item requirements
	// (hazmat, refrigerated, heavy) determine which methods are required/disabled.
	useEffect(() => {
		if (token == null || !initialCheckoutLoadedRef.current) return;
		if (validationVersion === 0) return;

		cartService
			.getCheckoutOptions(token)
			.then((res) => {
				if (res.success) {
					setCheckoutOptions(res.data);
					autoSelectShippingMethod(res.data);
				}
			})
			.catch(() => {});
	}, [token, validationVersion, autoSelectShippingMethod]);

	// Pre-flight validation — runs on load and re-runs after every cart mutation
	useEffect(() => {
		if (token == null || checkoutOptions == null) return;
		cartService
			.validateCart(token)
			.then((res) => {
				if (res.success) {
					setValidationErrors(res.data.errors);
					setValidationWarnings(res.data.warnings);
				}
			})
			.catch(() => {});
	}, [token, checkoutOptions, validationVersion]);

	// After adding a payment method, refresh checkout options so the new method
	// appears in the saved methods list and auto-select it.
	const handlePaymentMethodAdded = useCallback(async () => {
		setShowAddPaymentSidebar(false);
		if (token == null) return;

		try {
			const res = await cartService.getCheckoutOptions(token);
			if (res.success) {
				setCheckoutOptions(res.data);

				// Auto-select the newest saved method for the currently selected payment type
				const currentType = res.data.paymentTypes.find(
					(pt) => pt.id === paymentTypeId,
				);
				if (currentType != null) {
					const methodsForType = res.data.savedPaymentMethods.filter(
						(m) => m.type === currentType.paymentMethodType,
					);
					if (methodsForType.length > 0) {
						// Select the last one added (highest ID)
						const newest = methodsForType.reduce((a, b) =>
							a.id > b.id ? a : b,
						);
						setPaymentMethodId(newest.id);
					}
				}
			}
		} catch {
			// Non-critical — options will refresh on next page load
		}
	}, [token, paymentTypeId]);

	// --- Handlers ---

	// Aggregate debounced quantity updates — collects all pending changes across
	// items and flushes them together after 500ms of inactivity. Prevents rapid
	// edits on multiple items from firing separate racing API calls.
	const pendingQuantities = useRef<Record<number, number>>({});
	const aggregateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const flushAllPendingUpdates = useCallback(async () => {
		const pending = { ...pendingQuantities.current };
		pendingQuantities.current = {};
		aggregateTimer.current = null;

		const entries = Object.entries(pending);
		if (entries.length === 0 || token == null) {
			setHasPendingUpdates(false);
			return;
		}

		try {
			// Fire all updates in parallel — keep the last response as authoritative cart state
			const results = await Promise.all(
				entries.map(([detailId, qty]) =>
					cartService.updateCartItem(token, Number(detailId), {
						quantityOrdered: qty as number,
					}),
				),
			);
			let latestCart: PortalCartDto | null = null;
			for (const res of results) {
				if (res.data != null) latestCart = res.data;
			}
			if (latestCart != null) setCart(latestCart);
			triggerRevalidation();
		} catch {
			// On error, refetch to get accurate server state
			try {
				const res = await cartService.getCart(token);
				setCart(res.data);
			} catch {
				/* non-critical */
			}
		} finally {
			setHasPendingUpdates(false);
		}
	}, [token, setCart, triggerRevalidation]);

	const handleUpdateQuantity = useCallback(
		(detailId: number, quantityOrdered: number) => {
			if (quantityOrdered < 1) return;

			pendingQuantities.current[detailId] = quantityOrdered;
			setHasPendingUpdates(true);

			// Optimistic update — immediately reflect in the cart store
			const currentCart = useCartStore.getState().cart;
			if (currentCart != null) {
				const updatedItems = currentCart.lineItems.map((item) => {
					if (item.id === detailId) {
						const price = parseFloat(item.price ?? '0');
						return {
							...item,
							quantityOrdered,
							subtotal: (price * quantityOrdered).toFixed(2),
						};
					}
					return item;
				});
				const newSubtotal = updatedItems.reduce(
					(sum, item) => sum + parseFloat(item.subtotal ?? '0'),
					0,
				);
				setCart({
					...currentCart,
					lineItems: updatedItems,
					subtotal: newSubtotal.toFixed(2),
					total: newSubtotal.toFixed(2),
				});
			}

			// Reset the shared debounce timer — waits for 500ms of inactivity
			// across ALL items before flushing everything together
			if (aggregateTimer.current != null) {
				clearTimeout(aggregateTimer.current);
			}
			aggregateTimer.current = setTimeout(() => {
				void flushAllPendingUpdates();
			}, 500);
		},
		[setCart, flushAllPendingUpdates],
	);

	const handleRemoveItem = useCallback(
		async (detailId: number) => {
			if (token == null) return;
			setRemovingId(detailId);
			try {
				const res = await cartService.removeFromCart(token, detailId);
				setCart(res.data);
				triggerRevalidation();
			} catch {
			} finally {
				setRemovingId(null);
			}
		},
		[token, setCart, triggerRevalidation],
	);

	const handleClearCart = useCallback(async () => {
		if (token == null) return;
		setClearing(true);
		try {
			await cartService.clearCart(token);
			setCart(null);
			setShowClearConfirm(false);
		} catch {
			// Leave dialog open so user can retry or cancel
		} finally {
			setClearing(false);
		}
	}, [token, setCart]);

	const handleShippingAddressChange = useCallback(
		async (newId: number) => {
			setShippingAddressId(newId);
			if (token == null || billingAddressId == null) return;
			try {
				const res = await cartService.updateAddresses(token, {
					billingAddressId,
					shippingAddressId: newId,
				});
				if (res.success) {
					setCart(res.data);
					triggerRevalidation();
				}
			} catch {}
		},
		[token, billingAddressId, setCart, triggerRevalidation],
	);

	const handleBillingAddressChange = useCallback(
		async (newId: number) => {
			setBillingAddressId(newId);
			if (token == null || shippingAddressId == null) return;
			try {
				const res = await cartService.updateAddresses(token, {
					billingAddressId: newId,
					shippingAddressId,
				});
				if (res.success) {
					setCart(res.data);
					triggerRevalidation();
				}
			} catch {}
		},
		[token, shippingAddressId, setCart, triggerRevalidation],
	);

	// Portal checkout: Credit Card, ACH, and Company Credit
	const ecommercePaymentTypes = useMemo(
		() =>
			(checkoutOptions?.paymentTypes ?? []).filter((pt) =>
				['Credit Card', 'ACH', 'Company Credit'].includes(pt.paymentMethodType),
			),
		[checkoutOptions],
	);

	const selectedPaymentType = useMemo(
		() => ecommercePaymentTypes.find((pt) => pt.id === paymentTypeId) ?? null,
		[ecommercePaymentTypes, paymentTypeId],
	);

	// --- Shipping method derived state ---

	const selectedShippingMethod = useMemo(
		() =>
			checkoutOptions?.shippingMethods.find((m) => m.id === shippingMethodId) ??
			null,
		[checkoutOptions?.shippingMethods, shippingMethodId],
	);

	/** Compute effective shipping price based on fee, threshold, and current subtotal */
	const computeShippingEffectivePrice = useCallback(
		(method: PortalShippingMethodDto): string => {
			const fee = parseFloat(method.fee) || 0;
			const threshold =
				method.freeShippingThreshold != null
					? parseFloat(method.freeShippingThreshold)
					: null;
			const subtotal = parseFloat(cart?.subtotal ?? '0');

			if (threshold != null && !isNaN(threshold) && subtotal >= threshold) {
				return '0.00';
			}

			return fee.toFixed(2);
		},
		[cart?.subtotal],
	);

	// Shipping fee for the selected method (display-only, server calculates actual).
	// Includes the additional surcharge from client/item allowedShippingMethods if present.
	const shippingFeePreview = useMemo(() => {
		if (selectedShippingMethod == null) return 0;
		const basePrice = parseFloat(
			computeShippingEffectivePrice(selectedShippingMethod),
		);
		const surcharge =
			selectedShippingMethod.additionalFee != null
				? parseFloat(selectedShippingMethod.additionalFee)
				: 0;
		return basePrice + surcharge;
	}, [selectedShippingMethod, computeShippingEffectivePrice]);

	// Calculate fee preview based on selected payment type.
	// The server applies the actual fee at submission — this is display only.
	const feePreview = useMemo(() => {
		if (selectedPaymentType == null || selectedPaymentType.processingFee <= 0) {
			return 0;
		}
		const subtotal = parseFloat(cart?.subtotal ?? '0');
		if (selectedPaymentType.processingFeeType === '%') {
			return subtotal * (selectedPaymentType.processingFee / 100);
		}
		return selectedPaymentType.processingFee;
	}, [selectedPaymentType, cart?.subtotal]);

	// Calculate the display total including shipping fee and processing fee previews,
	// less any promo savings the engine has computed for this cart preview.
	const promoSavings = cart?.priced?.totalSavings ?? 0;
	const displayTotal = useMemo(() => {
		const subtotal = parseFloat(cart?.subtotal ?? '0');
		const freight = parseFloat(cart?.freight ?? '0');
		const tax = parseFloat(cart?.tax ?? '0');
		return (
			subtotal + freight + tax + shippingFeePreview + feePreview - promoSavings
		);
	}, [
		cart?.subtotal,
		cart?.freight,
		cart?.tax,
		shippingFeePreview,
		feePreview,
		promoSavings,
	]);

	// Filter validation messages based on payment context.
	// Credit-related issues only matter when paying on account.
	const creditValidationTypes = new Set(['creditLimit', 'creditHold']);
	const isPayOnAccount =
		selectedPaymentType?.paymentMethodType === 'Company Credit';

	const filteredErrors = useMemo(
		() =>
			isPayOnAccount
				? validationErrors
				: validationErrors.filter((e) => !creditValidationTypes.has(e.type)),
		[validationErrors, isPayOnAccount],
	);

	const filteredWarnings = useMemo(
		() =>
			isPayOnAccount
				? validationWarnings
				: validationWarnings.filter((w) => !creditValidationTypes.has(w.type)),
		[validationWarnings, isPayOnAccount],
	);

	// Split into cart-level (no itemId) and item-level (has itemId) for display.
	// Cart-level issues show in the top alert box. Item-level issues render inline on each item.
	const cartLevelErrors = useMemo(
		() => filteredErrors.filter((e) => e.itemId == null),
		[filteredErrors],
	);
	const cartLevelWarnings = useMemo(
		() => filteredWarnings.filter((w) => w.itemId == null),
		[filteredWarnings],
	);
	const itemErrorMap = useMemo(() => {
		const map = new Map<number, Array<ValidationIssue>>();
		for (const err of filteredErrors) {
			if (err.itemId != null) {
				const existing = map.get(err.itemId) ?? [];
				existing.push(err);
				map.set(err.itemId, existing);
			}
		}
		return map;
	}, [filteredErrors]);
	const itemWarningMap = useMemo(() => {
		const map = new Map<number, Array<ValidationIssue>>();
		for (const warn of filteredWarnings) {
			if (warn.itemId != null) {
				const existing = map.get(warn.itemId) ?? [];
				existing.push(warn);
				map.set(warn.itemId, existing);
			}
		}
		return map;
	}, [filteredWarnings]);

	const canSubmit = useMemo(() => {
		if (shippingAddressId == null || billingAddressId == null) return false;
		if (paymentTypeId == null) return false;
		// Require a shipping method selection when shipping options are available
		const hasShippingOptions = (checkoutOptions?.shippingMethods ?? []).some(
			(m) => !m.isDisabled,
		);
		if (hasShippingOptions && shippingMethodId == null) return false;
		const needsSavedMethod =
			selectedPaymentType != null &&
			['Credit Card', 'ACH'].includes(selectedPaymentType.paymentMethodType);
		if (needsSavedMethod && paymentMethodId == null) return false;
		if (filteredErrors.length > 0) return false;
		return true;
	}, [
		shippingAddressId,
		billingAddressId,
		paymentTypeId,
		paymentMethodId,
		selectedPaymentType,
		filteredErrors,
		checkoutOptions?.shippingMethods,
		shippingMethodId,
	]);

	const handleSubmit = useCallback(async () => {
		if (
			token == null ||
			!canSubmit ||
			shippingAddressId == null ||
			billingAddressId == null
		)
			return;
		setSubmitting(true);
		setSubmitError(null);

		try {
			const res = await cartService.submitCart(token, {
				billingAddressId,
				shippingAddressId,
				shippingMethodId: shippingMethodId ?? undefined,
				paymentMethodId: paymentMethodId ?? undefined,
				paymentMethodTypeId: paymentTypeId ?? undefined,
				paymentMethodType: selectedPaymentType?.paymentMethodType,
				customerPO:
					customerPO.trim().length > 0 ? customerPO.trim() : undefined,
			});

			if (res.success && res.data != null) {
				setCart(null);
				navigate(`/orders/${res.data.orderId}`);
			} else if (res.errors != null && res.errors.length > 0) {
				setValidationErrors(res.errors);
			}
		} catch (error) {
			setSubmitError((error as Error).message ?? t('Failed to place order.'));
		} finally {
			setSubmitting(false);
		}
	}, [
		token,
		canSubmit,
		shippingAddressId,
		billingAddressId,
		shippingMethodId,
		paymentMethodId,
		paymentTypeId,
		selectedPaymentType,
		customerPO,
		setCart,
		navigate,
		t,
	]);

	// --- Render ---

	if (isLoading) {
		return (
			<div className="px-4 py-12 text-center">
				<FontAwesomeIcon
					icon={faSpinner}
					className="animate-spin text-2xl text-gray-400 mb-3"
				/>
				<p className="text-sm text-brand-muted">{t('Loading cart...')}</p>
			</div>
		);
	}

	if (lineItems.length === 0) {
		return (
			<div className="px-4 py-12 text-center max-w-md mx-auto">
				<FontAwesomeIcon
					icon={faCartShopping}
					className="text-4xl text-gray-300 dark:text-gray-600 mb-4"
				/>
				<h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
					{t('Your cart is empty')}
				</h1>
				<p className="text-sm text-brand-muted mb-6">
					{t('Browse our catalog to find products.')}
				</p>
				<button
					type="button"
					onClick={() => navigate('/catalog')}
					className="px-6 py-3 bg-portal-accent hover:bg-portal-accent-hover text-white font-medium rounded-lg transition-colors min-h-touch"
				>
					{t('Start Shopping')}
				</button>
			</div>
		);
	}

	return (
		<div className="px-4 py-4 max-w-3xl mx-auto pb-32">
			<div className="flex items-center justify-between gap-3 mb-4">
				<button
					type="button"
					onClick={() => navigate('/catalog')}
					className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors min-h-touch"
				>
					<FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
					<span>{t('Continue Shopping')}</span>
				</button>
				<button
					type="button"
					onClick={() => setShowClearConfirm(true)}
					className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:underline transition-colors min-h-touch"
				>
					<FontAwesomeIcon icon={faTrash} className="text-xs" />
					<span>{t('Clear Cart')}</span>
				</button>
			</div>
			<h1 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
				{t('Cart')} ({lineItems.length})
			</h1>

			{/* ---- CART ITEMS ---- */}
			<div className="space-y-3 mb-6">
				{lineItems.map((item) => {
					const itemErrors = itemErrorMap.get(item.fkItemId) ?? [];
					const itemWarnings = itemWarningMap.get(item.fkItemId) ?? [];
					const hasItemErrors = itemErrors.length > 0;
					const hasItemWarnings = itemWarnings.length > 0;

					return (
						<div
							key={item.id}
							className={`flex flex-wrap gap-3 bg-brand-surface border rounded-xl p-3 ${
								hasItemErrors
									? 'border-red-300 dark:border-red-700'
									: hasItemWarnings
										? 'border-amber-300 dark:border-amber-700'
										: 'border-brand-border'
							}`}
						>
							<div
								className="w-16 h-16 shrink-0 bg-brand-input rounded-lg flex items-center justify-center overflow-hidden cursor-pointer"
								onClick={() => navigate(`/product/${item.fkItemId}`)}
							>
								<ResilientImage
									src={item.primaryImageUrl}
									alt={t(item.description)}
									className="w-full h-full object-cover"
									onLikelyStaleUrl={refreshCartForStaleImage}
									fallback={
										<FontAwesomeIcon
											icon={faImage}
											className="text-gray-400 dark:text-gray-500 text-lg"
											aria-label={t('No image available')}
										/>
									}
								/>
							</div>
							<div className="flex-1 min-w-0">
								<h3
									className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate cursor-pointer hover:text-portal-accent"
									onClick={() => navigate(`/product/${item.fkItemId}`)}
								>
									{t(item.description)}
								</h3>
								<p className="text-xs text-brand-muted">
									{t(item.itemExternalId)}
								</p>
								<ItemDesignationBadges designations={item.designations} />
								{(() => {
									const pl = pricedLineByDetailId.get(item.id);
									const promos = pl?.appliedPromos ?? [];
									if (promos.length === 0) return null;
									// Promo-name chip(s) under the row's metadata. The chip
									// names the campaign that produced the savings; the
									// strikethrough on the right names the savings amount.
									// Multi-promo lines stack chips horizontally with wrap.
									// Migrated to the unified PromoChip — same visual as
									// the catalog/detail/order surfaces.
									return (
										<div className="mt-1 flex flex-wrap gap-1">
											{promos.map((p) => (
												<PromoChip
													key={p.promoName}
													variant="inline"
													name={t(p.promoName)}
												/>
											))}
										</div>
									);
								})()}
								{checkoutOptions?.itemSurcharges?.[item.fkItemId] != null && (
									<p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
										{t(checkoutOptions.itemSurcharges[item.fkItemId])}
									</p>
								)}
								<div className="flex items-center gap-1.5 mt-2">
									<button
										type="button"
										onClick={() =>
											handleUpdateQuantity(item.id, item.quantityOrdered - 1)
										}
										disabled={item.quantityOrdered <= 1}
										className="w-7 h-7 flex items-center justify-center rounded border border-brand-border text-gray-600 dark:text-gray-300 disabled:opacity-30"
										aria-label={t('Decrease quantity')}
									>
										<FontAwesomeIcon icon={faMinus} className="text-[10px]" />
									</button>
									<input
										type="number"
										min={1}
										value={item.quantityOrdered}
										onChange={(e) => {
											const val = parseInt(e.target.value, 10);
											if (!isNaN(val) && val >= 1) {
												handleUpdateQuantity(item.id, val);
											}
										}}
										className="w-12 h-7 text-center text-sm font-medium rounded border border-brand-border bg-brand-surface text-gray-900 dark:text-gray-100 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
										aria-label={t('Quantity')}
									/>
									<button
										type="button"
										onClick={() =>
											handleUpdateQuantity(item.id, item.quantityOrdered + 1)
										}
										className="w-7 h-7 flex items-center justify-center rounded border border-brand-border text-gray-600 dark:text-gray-300"
										aria-label={t('Increase quantity')}
									>
										<FontAwesomeIcon icon={faPlus} className="text-[10px]" />
									</button>
								</div>
							</div>
							<div className="flex flex-col items-end justify-between">
								{(() => {
									const pl = pricedLineByDetailId.get(item.id);
									const hasDiscount = pl != null && pl.lineDiscountTotal > 0;
									if (hasDiscount) {
										// On-sale line: strikethrough original + discounted
										// final. The math (how much was saved) is easy to read
										// from the price drop itself; the WHY (which promo
										// triggered it) shows as a chip on the title side of
										// the row, so the price column stays clean.
										return (
											<div className="flex flex-col items-end leading-tight">
												<p className="text-xs text-brand-muted line-through">
													{formatCurrency(pl.originalSubtotal.toFixed(2))}
												</p>
												<p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
													{formatCurrency(pl.finalSubtotal.toFixed(2))}
												</p>
											</div>
										);
									}
									return (
										<p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
											{formatCurrency(item.subtotal)}
										</p>
									);
								})()}
								<button
									type="button"
									onClick={() => handleRemoveItem(item.id)}
									disabled={removingId === item.id}
									className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 disabled:opacity-60 disabled:cursor-not-allowed"
									aria-label={t('Remove item')}
								>
									<FontAwesomeIcon
										icon={removingId === item.id ? faSpinner : faTrash}
										className="text-sm"
										spin={removingId === item.id}
									/>
								</button>
							</div>
							{/* Item-specific validation issues — inline below the item content */}
							{(hasItemErrors || hasItemWarnings) && (
								<div className="basis-full mt-1 space-y-0.5 pl-[76px]">
									{itemErrors.map((err, i) => (
										<p
											key={`e${i}`}
											className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400"
										>
											<FontAwesomeIcon
												icon={faCircleExclamation}
												className="text-[10px] shrink-0"
											/>
											{t(err.message)}
										</p>
									))}
									{itemWarnings.map((warn, i) => (
										<p
											key={`w${i}`}
											className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400"
										>
											<FontAwesomeIcon
												icon={faTriangleExclamation}
												className="text-[10px] shrink-0"
											/>
											{t(warn.message)}
										</p>
									))}
								</div>
							)}
						</div>
					);
				})}
			</div>

			{/* ---- CHECKOUT OPTIONS ---- */}
			{checkoutLoading && (
				<div className="py-6 text-center">
					<FontAwesomeIcon
						icon={faSpinner}
						className="animate-spin text-gray-400"
					/>
				</div>
			)}

			{checkoutOptions != null && (
				<>
					{/* Cart-level validation errors (credit hold, min order, etc.) */}
					{cartLevelErrors.length > 0 && (
						<div className="mb-4 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl">
							<div className="flex items-start gap-2">
								<FontAwesomeIcon
									icon={faCircleExclamation}
									className="text-red-500 mt-0.5"
								/>
								<div className="text-sm">
									{cartLevelErrors.map((err, i) => (
										<p key={i} className="text-red-700 dark:text-red-300">
											{t(err.message)}
										</p>
									))}
								</div>
							</div>
						</div>
					)}

					{/* Cart-level validation warnings */}
					{cartLevelWarnings.length > 0 && (
						<div className="mb-4 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl">
							<div className="flex items-start gap-2">
								<FontAwesomeIcon
									icon={faTriangleExclamation}
									className="text-amber-500 mt-0.5"
								/>
								<div className="text-sm">
									{cartLevelWarnings.map((warn, i) => (
										<p key={i} className="text-amber-700 dark:text-amber-300">
											{t(warn.message)}
										</p>
									))}
								</div>
							</div>
						</div>
					)}

					{/* Shipping Address */}
					<Section title={t('Shipping Address')}>
						<AddressSelector
							addresses={checkoutOptions.shippingAddresses}
							selectedId={shippingAddressId}
							onChange={handleShippingAddressChange}
						/>
					</Section>

					{/* Billing Address */}
					<Section title={t('Billing Address')}>
						<AddressSelector
							addresses={checkoutOptions.billingAddresses}
							selectedId={billingAddressId}
							onChange={handleBillingAddressChange}
						/>
					</Section>

					{/* Shipping Method */}
					{checkoutOptions.isShippingConfigured && (
						<Section title={t('Shipping Method')}>
							{checkoutOptions.hasShippingConflict ? (
								<div className="p-3 rounded-lg border border-red-300 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10">
									<div className="flex items-start gap-2">
										<FontAwesomeIcon
											icon={faTriangleExclamation}
											className="text-red-500 mt-0.5"
										/>
										<p className="text-sm text-red-700 dark:text-red-300">
											{t(
												checkoutOptions.shippingConflictMessage ??
													'This order contains items with conflicting shipping requirements. Please split the order into separate orders.',
											)}
										</p>
									</div>
								</div>
							) : checkoutOptions.shippingMethods.length === 0 ? (
								<p className="text-sm text-brand-muted italic">
									{t('No shipping methods available')}
								</p>
							) : (
								<div className="space-y-2">
									{checkoutOptions.shippingMethods.map((method) => {
										const isSelected = shippingMethodId === method.id;
										const isDisabled = method.isDisabled;
										const displayPrice = computeShippingEffectivePrice(method);

										return (
											<button
												key={method.id}
												type="button"
												onClick={() => {
													if (!isDisabled) {
														setShippingMethodId(method.id);
													}
												}}
												disabled={isDisabled}
												className={`w-full text-left border rounded-lg p-3 transition-colors ${
													isDisabled
														? 'opacity-50 cursor-not-allowed border-brand-border bg-brand-input'
														: isSelected
															? 'border-portal-accent bg-portal-accent/10 dark:bg-portal-accent/10'
															: 'border-brand-border bg-brand-surface hover:border-gray-300 dark:hover:border-gray-600'
												}`}
											>
												<div className="flex items-center">
													<FontAwesomeIcon
														icon={faTruck}
														className={`text-sm ${
															isSelected
																? 'text-portal-accent'
																: 'text-brand-muted'
														}`}
													/>
													<div className="flex-1 ml-3 min-w-0">
														<div className="flex items-center gap-2">
															<span
																className={`text-sm font-medium ${
																	isSelected
																		? 'text-portal-accent'
																		: 'text-gray-900 dark:text-gray-100'
																}`}
															>
																{t(method.label)}
															</span>
															<span
																className={`text-sm font-medium ml-auto ${
																	displayPrice === '0.00'
																		? 'text-green-600 dark:text-green-400'
																		: 'text-gray-900 dark:text-gray-100'
																}`}
															>
																{displayPrice === '0.00'
																	? t('Free')
																	: formatCurrency(parseFloat(displayPrice))}
															</span>
														</div>
														{method.description != null &&
															method.description !== '' && (
																<p className="text-xs text-brand-muted mt-1">
																	{t(method.description)}
																</p>
															)}
														{method.freeShippingThreshold != null &&
															displayPrice !== '0.00' && (
																<p className="text-xs text-green-600 dark:text-green-400 mt-1">
																	{t('Free shipping on orders over')}{' '}
																	{formatCurrency(
																		parseFloat(method.freeShippingThreshold),
																	)}
																</p>
															)}
														{method.additionalFee != null && (
															<p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
																{t('Includes')}{' '}
																{formatCurrency(
																	parseFloat(method.additionalFee),
																)}{' '}
																{t('surcharge')}
															</p>
														)}
														{isDisabled && method.disabledReason != null && (
															<p className="text-xs text-red-600 dark:text-red-400 mt-1">
																{t(method.disabledReason)}
															</p>
														)}
													</div>
												</div>
											</button>
										);
									})}
								</div>
							)}
							{checkoutOptions.shippingDisclaimer != null && (
								<p className="text-xs text-brand-muted mt-2">
									{t(checkoutOptions.shippingDisclaimer)}
								</p>
							)}
						</Section>
					)}

					{/* Payment */}
					<Section title={t('Payment')}>
						<div className="space-y-2">
							{ecommercePaymentTypes.map((pt) => (
								<PaymentTypeOption
									key={pt.id}
									paymentType={pt}
									isSelected={paymentTypeId === pt.id}
									onSelect={() => {
										setPaymentTypeId(pt.id);
										const methodsForType =
											checkoutOptions.savedPaymentMethods.filter(
												(m) => m.type === pt.paymentMethodType,
											);
										const primary = methodsForType.find((m) => m.isPrimary);
										setPaymentMethodId(
											primary?.id ?? methodsForType[0]?.id ?? null,
										);
									}}
									savedMethods={checkoutOptions.savedPaymentMethods.filter(
										(m) => m.type === pt.paymentMethodType,
									)}
									selectedMethodId={paymentMethodId}
									onMethodSelect={setPaymentMethodId}
									creditInfo={checkoutOptions.credit}
									onAddPaymentMethod={() => setShowAddPaymentSidebar(true)}
									onManagePaymentMethods={() => navigate('/account')}
								/>
							))}
						</div>
					</Section>

					{/* Customer PO */}
					<Section title={t('Customer PO')}>
						<input
							type="text"
							value={customerPO}
							onChange={(e) => setCustomerPO(e.target.value)}
							placeholder={t('Optional')}
							className="w-full px-3 py-2.5 rounded-lg bg-brand-input border border-brand-border text-sm text-gray-900 dark:text-gray-100"
						/>
					</Section>

					{/* Minimum order warning */}
					{checkoutOptions.minimumOrderAmount != null &&
						parseFloat(cart?.subtotal ?? '0') <
							checkoutOptions.minimumOrderAmount && (
							<div className="mb-4 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl text-sm">
								<div className="flex items-center gap-2">
									<FontAwesomeIcon
										icon={faTriangleExclamation}
										className="text-amber-500"
									/>
									<span className="text-amber-700 dark:text-amber-300">
										{t('Minimum order amount')}:{' '}
										{formatCurrency(checkoutOptions.minimumOrderAmount)}
									</span>
								</div>
							</div>
						)}
				</>
			)}

			{/* ---- ORDER SUMMARY + SUBMIT ----
			    Sticky on tablet/desktop so the summary stays in view next
			    to the cart line items. NOT sticky on mobile: the summary
			    card is a chunky block (subtotal/shipping/tax/fees/total +
			    button) that would eat ~200px of viewport at all times,
			    and the previous `sticky bottom-20` caused the Place Order
			    button to hide behind the keyboard when the user focused
			    the Customer PO field — both the BottomNav and the sticky
			    CTA anchor to innerHeight, but Android keyboards shrink
			    visualViewport.height instead, so bottom-anchored UI ends
			    up occluded. Modern mobile e-comm (Amazon, Shopify default,
			    Walmart) places a regular non-sticky summary at the foot
			    of the cart on mobile, and sticks only on wider screens. */}
			{/* Coupon entry + applied codes + almost-qualified hints —
				    rendered above the totals card so the customer's eye flows
				    "what's in cart → savings/qualifier opportunities → totals". */}
			<CartPromoPanel
				appliedCouponCodes={cart?.appliedCouponCodes ?? []}
				priced={cart?.priced ?? null}
				onCartUpdate={setCart}
				token={token}
			/>

			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 md:sticky md:bottom-4">
				<h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
					{t('Order Summary')}
				</h2>
				<div className="space-y-1 mb-4 text-sm">
					<SummaryRow
						label={`${t('Items')} (${lineItems.length})`}
						value={formatCurrency(cart?.subtotal)}
					/>
					{shippingFeePreview > 0 && (
						<SummaryRow
							label={t('Shipping')}
							value={formatCurrency(shippingFeePreview)}
						/>
					)}
					{cart?.tax != null && parseFloat(cart.tax) > 0 && (
						<SummaryRow label={t('Tax')} value={formatCurrency(cart.tax)} />
					)}
					{feePreview > 0 && (
						<SummaryRow
							label={t('Processing Fee')}
							value={formatCurrency(feePreview)}
						/>
					)}
					{promoSavings > 0 &&
						(() => {
							const promos = cart?.priced?.appliedPromos ?? [];
							// Single promo: just show the named promo line — the
							// "Savings" parent row would just repeat the same number,
							// which reads as duplicated. Multiple promos: show the
							// aggregate Savings total + indented per-promo breakdown.
							if (promos.length === 1) {
								const p = promos[0];
								return (
									<div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400">
										<span className="font-medium truncate">
											{t(p.promoName)}
											{p.couponCodeUsed != null && (
												<span className="ml-1 text-brand-muted text-xs">
													({t(p.couponCodeUsed)})
												</span>
											)}
										</span>
										<span>−{formatCurrency(p.totalDiscountAmount)}</span>
									</div>
								);
							}
							return (
								<>
									<div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400">
										<span className="font-medium">{t('Savings')}</span>
										<span>−{formatCurrency(promoSavings)}</span>
									</div>
									{promos.map((promo) => (
										<div
											key={promo.promoId}
											className="flex items-center justify-between text-xs text-emerald-700/80 dark:text-emerald-300/80 pl-2"
										>
											<span className="truncate">
												{t(promo.promoName)}
												{promo.couponCodeUsed != null && (
													<span className="ml-1 text-brand-muted">
														({t(promo.couponCodeUsed)})
													</span>
												)}
											</span>
											<span>−{formatCurrency(promo.totalDiscountAmount)}</span>
										</div>
									))}
								</>
							);
						})()}
					<div className="flex items-center justify-between font-semibold text-gray-900 dark:text-gray-100 pt-2 border-t border-gray-100 dark:border-gray-800">
						<span>{t('Total')}</span>
						<span>{formatCurrency(displayTotal)}</span>
					</div>
				</div>

				{submitError != null && (
					<p className="text-sm text-red-600 dark:text-red-400 mb-3">
						{t(submitError)}
					</p>
				)}

				<button
					type="button"
					onClick={handleSubmit}
					disabled={
						!canSubmit ||
						submitting ||
						checkoutOptions == null ||
						hasPendingUpdates
					}
					className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent hover:bg-portal-accent-hover disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors min-h-touch"
				>
					{(submitting || hasPendingUpdates) && (
						<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
					)}
					{hasPendingUpdates
						? t('Updating cart...')
						: submitting
							? t('Placing Order...')
							: t('Place Order')}
				</button>
			</div>

			{/* Clear-cart confirmation */}
			<ConfirmDialog
				isOpen={showClearConfirm}
				title={t('Clear cart?')}
				message={t(
					`This will remove all ${lineItems.length} item(s) from your cart. This action cannot be undone.`,
				)}
				confirmText={clearing ? t('Clearing...') : t('Clear Cart')}
				cancelText={t('Cancel')}
				variant="danger"
				onCancel={() => {
					if (!clearing) setShowClearConfirm(false);
				}}
				onConfirm={() => void handleClearCart()}
			/>

			{/* Add Payment Method sidebar */}
			<Sidebar
				isOpen={showAddPaymentSidebar}
				onClose={() => setShowAddPaymentSidebar(false)}
				title={t('Add Payment Method')}
				width="md"
				position="right"
				modal={true}
			>
				<div className="p-4">
					<PortalAddPaymentMethod
						onSuccess={() => void handlePaymentMethodAdded()}
						onCancel={() => setShowAddPaymentSidebar(false)}
						supportedPaymentMethods={checkoutOptions?.paymentTypes}
						preSelectedPaymentType={
							selectedPaymentType?.paymentMethodType === 'ACH'
								? PaymentMethodTypeEnum.ACH
								: selectedPaymentType?.paymentMethodType === 'Credit Card'
									? PaymentMethodTypeEnum.CREDIT_CARD
									: undefined
						}
					/>
				</div>
			</Sidebar>
		</div>
	);
}

// --- Sub-components ---

function Section({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}): JSX.Element {
	return (
		<div className="mb-4">
			<h2 className="text-xs font-semibold text-brand-muted uppercase tracking-wide mb-2">
				{title}
			</h2>
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4">
				{children}
			</div>
		</div>
	);
}

function AddressSelector({
	addresses,
	selectedId,
	onChange,
}: {
	addresses: Array<PortalCheckoutAddressDto>;
	selectedId: number | null;
	onChange: (id: number) => void;
}): JSX.Element {
	const { t } = useTranslation();

	if (addresses.length === 0) {
		return (
			<p className="text-sm text-brand-muted">{t('No addresses available')}</p>
		);
	}

	return (
		<select
			value={selectedId ?? ''}
			onChange={(e) => onChange(parseInt(e.target.value, 10))}
			className="w-full px-3 py-2.5 rounded-lg bg-brand-input border border-brand-border text-sm text-gray-900 dark:text-gray-100"
		>
			{addresses.map((addr) => (
				<option key={addr.id} value={addr.id}>
					{t(
						[addr.address1, addr.city, addr.state, addr.zip]
							.filter(Boolean)
							.join(', '),
					)}
				</option>
			))}
		</select>
	);
}

function PaymentTypeOption({
	paymentType,
	isSelected,
	onSelect,
	savedMethods,
	selectedMethodId,
	onMethodSelect,
	creditInfo,
	onAddPaymentMethod,
	onManagePaymentMethods,
}: {
	paymentType: PortalCheckoutPaymentTypeDto;
	isSelected: boolean;
	onSelect: () => void;
	savedMethods: Array<PortalCheckoutPaymentMethodDto>;
	selectedMethodId: number | null;
	onMethodSelect: (id: number) => void;
	creditInfo?: PortalClientCreditDto | null;
	onAddPaymentMethod: () => void;
	onManagePaymentMethods: () => void;
}): JSX.Element {
	const { t } = useTranslation();

	const typeLabels: Record<string, string> = {
		'Credit Card': 'Credit Card',
		ACH: 'ACH / Bank Transfer',
		'Company Credit': 'Pay on Account',
		Cash: 'Cash',
		Check: 'Check',
	};

	const label =
		typeLabels[paymentType.paymentMethodType] ?? paymentType.paymentMethodType;
	const showMethodSelector =
		isSelected &&
		['Credit Card', 'ACH'].includes(paymentType.paymentMethodType) &&
		savedMethods.length > 0;

	return (
		<div>
			<label className="flex items-center gap-3 cursor-pointer p-2 -mx-2 rounded-lg hover:bg-brand-input">
				<input
					type="radio"
					name="paymentType"
					checked={isSelected}
					onChange={onSelect}
					className="w-4 h-4 text-portal-accent"
				/>
				<FontAwesomeIcon icon={faCreditCard} className="text-gray-400" />
				<span className="text-sm text-gray-900 dark:text-gray-100">
					{t(label)}
				</span>
				{paymentType.processingFee > 0 && (
					<span className="text-xs text-gray-400 ml-auto">
						{paymentType.processingFeeType === '%'
							? `${paymentType.processingFee}% ${t('fee')}`
							: `${formatCurrency(paymentType.processingFee)} ${t('fee')}`}
					</span>
				)}
			</label>
			{showMethodSelector && (
				<div className="ml-9 mt-1 mb-2">
					<select
						value={selectedMethodId ?? ''}
						onChange={(e) => onMethodSelect(parseInt(e.target.value, 10))}
						className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-gray-900 dark:text-gray-100"
					>
						{savedMethods.map((m) => (
							<option key={m.id} value={m.id}>
								{t(m.subtype ?? m.type)}{' '}
								{m.lastFour != null ? `****${m.lastFour}` : ''}{' '}
								{m.name != null ? `(${t(m.name)})` : ''}
							</option>
						))}
					</select>
				</div>
			)}
			{isSelected &&
				['Credit Card', 'ACH'].includes(paymentType.paymentMethodType) && (
					<div className="ml-9 mt-1 mb-2 flex items-center gap-3">
						{savedMethods.length === 0 && (
							<span className="text-xs text-brand-muted">
								{t('No saved payment methods.')}
							</span>
						)}
						<button
							type="button"
							onClick={onAddPaymentMethod}
							className="text-xs font-medium text-portal-accent hover:underline"
						>
							{t('+ Add new')}
						</button>
						<button
							type="button"
							onClick={onManagePaymentMethods}
							className="text-xs text-brand-muted hover:underline"
						>
							{t('Manage')}
						</button>
					</div>
				)}
			{/* Credit info nested under Pay on Account when selected */}
			{isSelected === true &&
			paymentType.paymentMethodType === 'Company Credit' &&
			creditInfo != null &&
			creditInfo.creditLimit != null ? (
				<div className="ml-9 mt-1 mb-2 p-3 bg-portal-accent/10 dark:bg-portal-accent/10 border border-portal-accent/30 rounded-lg text-sm">
					<div className="flex justify-between text-gray-600 dark:text-gray-400">
						<span>{t('Credit Limit')}</span>
						<span>{formatCurrency(creditInfo.creditLimit)}</span>
					</div>
					<div className="flex justify-between text-gray-600 dark:text-gray-400">
						<span>{t('Current Balance')}</span>
						<span>{formatCurrency(creditInfo.creditBalance)}</span>
					</div>
					<div className="flex justify-between font-medium text-gray-900 dark:text-gray-100 pt-1 border-t border-portal-accent/20 mt-1">
						<span>{t('Available Credit')}</span>
						<span>{formatCurrency(creditInfo.availableCredit)}</span>
					</div>
					{creditInfo.hasCreditHold === true && (
						<p className="text-red-600 dark:text-red-400 mt-2 font-medium">
							{t('Account is on credit hold')}
						</p>
					)}
				</div>
			) : null}
		</div>
	);
}

function SummaryRow({
	label,
	value,
}: {
	label: string;
	value: string | null;
}): JSX.Element {
	return (
		<div className="flex items-center justify-between text-gray-600 dark:text-gray-400">
			<span>{label}</span>
			<span>{value ?? '$0.00'}</span>
		</div>
	);
}

/**
 * Cart promo panel — coupon entry + currently-applied codes + "almost
 * qualified" hints. Sits above the order summary so the customer sees
 * opportunities for additional savings before they place the order.
 *
 * Submitting a coupon hits POST /api/portal/cart/coupons (rate-limited
 * server-side at 10/min and 60/hour per user). The cart response that
 * comes back already includes the re-evaluated `priced` block so the
 * order summary updates in the same render.
 */
function CartPromoPanel({
	appliedCouponCodes,
	priced,
	onCartUpdate,
	token,
}: {
	appliedCouponCodes: Array<string>;
	priced: PricedCart | null;
	onCartUpdate: (cart: PortalCartDto | null) => void;
	token: string | null;
}): JSX.Element | null {
	const { t } = useTranslation();
	const [code, setCode] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [removingCode, setRemovingCode] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	const unfulfilled = priced?.unfulfilledPromos ?? [];

	// Keep the panel out of the DOM entirely when there's nothing to show
	// AND no auth token (anonymous browsers can't have coupons / hints).
	if (
		token == null &&
		appliedCouponCodes.length === 0 &&
		unfulfilled.length === 0
	) {
		return null;
	}

	const apply = async (raw: string) => {
		const trimmed = raw.trim();
		if (trimmed.length === 0 || token == null) {
			return;
		}
		setError(null);
		setSubmitting(true);
		try {
			const res = await cartService.applyCartCoupon(token, trimmed);
			if (res.success) {
				onCartUpdate(res.data);
				setCode('');
			} else {
				setError(t('Could not apply that code. Try again.'));
			}
		} catch (err) {
			// Rate-limit / 4xx / network — surface a friendly message.
			const message =
				err instanceof Error && err.message.length > 0
					? err.message
					: t('Could not apply that code. Try again.');
			setError(message);
		} finally {
			setSubmitting(false);
		}
	};

	const remove = async (codeToRemove: string) => {
		if (token == null) return;
		setError(null);
		setRemovingCode(codeToRemove);
		try {
			const res = await cartService.removeCartCoupon(token, codeToRemove);
			if (res.success) {
				onCartUpdate(res.data);
			}
		} catch {
			setError(t('Could not remove that code. Try again.'));
		} finally {
			setRemovingCode(null);
		}
	};

	return (
		<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4 space-y-3">
			{/* Almost-qualified hints — surfaced first so customers see the
			    "spend $X more" / "add Y more" before the coupon entry. */}
			{unfulfilled.length > 0 && (
				<div className="space-y-1.5">
					{unfulfilled.map((u) => (
						<div
							key={u.promoId}
							className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-2.5 py-1.5"
						>
							<FontAwesomeIcon
								icon={faTriangleExclamation}
								className="mt-0.5 text-amber-500 shrink-0"
							/>
							<span>{t(u.hint)}</span>
						</div>
					))}
				</div>
			)}

			{/* Coupon entry — only shown to authenticated visitors (token != null). */}
			{token != null && (
				<>
					<div className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
						<FontAwesomeIcon icon={faTag} className="text-portal-accent" />
						{t('Promo Code')}
					</div>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							void apply(code);
						}}
						className="flex items-center gap-2"
					>
						<input
							type="text"
							value={code}
							onChange={(e) => setCode(e.target.value)}
							placeholder={t('Enter code')}
							disabled={submitting}
							className="flex-1 min-w-0 px-3 py-2 text-sm rounded-lg border border-brand-border bg-brand-input text-gray-900 dark:text-gray-100 placeholder:text-brand-muted disabled:opacity-60"
							autoComplete="off"
							spellCheck={false}
						/>
						<button
							type="submit"
							disabled={submitting || code.trim().length === 0}
							className="px-3 py-2 text-sm font-medium bg-portal-accent hover:bg-portal-accent-hover disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg min-h-touch"
						>
							{submitting ? (
								<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
							) : (
								t('Apply')
							)}
						</button>
					</form>
					{error != null && (
						<p className="text-xs text-red-600 dark:text-red-400">{t(error)}</p>
					)}

					{appliedCouponCodes.length > 0 && (
						<div className="flex flex-wrap gap-1.5">
							{appliedCouponCodes.map((c) => (
								<span
									key={c}
									className="inline-flex items-center gap-1.5 text-xs font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 rounded-full pl-2.5 pr-1.5 py-1"
								>
									{t(c)}
									<button
										type="button"
										onClick={() => void remove(c)}
										disabled={removingCode === c}
										className="w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-emerald-200/70 dark:hover:bg-emerald-700/40 disabled:opacity-60"
										aria-label={t('Remove coupon')}
									>
										<FontAwesomeIcon
											icon={removingCode === c ? faSpinner : faTimes}
											spin={removingCode === c}
											className="text-[9px]"
										/>
									</button>
								</span>
							))}
						</div>
					)}
				</>
			)}
		</div>
	);
}
